/**
 * Phase 5: Spieler pflegen eigene Notizen und ihren Charakter. Die Regeln hier sind
 * rein (ohne Datenbank) und werden vom Server angewendet.
 */
import type { Frontmatter } from './frontmatter.js';
import { hasSecrets, stripSecrets } from './secrets.js';
import type { Visibility } from './noteTypes.js';

/** Typen, die Spieler selbst anlegen dürfen. */
export const PLAYER_NOTE_TYPES = ['notiz', 'charakter'] as const;

/** Ordner für die Notizen eines Spielers, z. B. `Spieler/Anna`. */
export function playerFolder(userName: string): string {
  const safe = userName.normalize('NFC').replace(/[\\/:*?"<>|#^[\]]/g, '_').trim() || 'Spieler';
  return `Spieler/${safe}`;
}

function valueHasSecrets(v: unknown): boolean {
  if (typeof v === 'string') return hasSecrets(v);
  if (Array.isArray(v)) return v.some(valueHasSecrets);
  if (v && typeof v === 'object') return Object.values(v).some(valueHasSecrets);
  return false;
}

/** Schlüssel, deren Wert Geheimes enthält – Spieler sehen ihn nur gefiltert und dürfen ihn nicht ersetzen. */
export function secretFrontmatterKeys(fm: Frontmatter): Set<string> {
  return new Set(Object.entries(fm).filter(([, v]) => valueHasSecrets(v)).map(([k]) => k));
}

/**
 * Eigenschaften, die ein Spieler speichert, mit dem Serverstand zusammenführen:
 * Werte mit Geheimnissen bleiben unverändert (der Spieler kennt sie nur gefiltert).
 */
export function mergePlayerFrontmatter(current: Frontmatter, incoming: Frontmatter): Frontmatter {
  const out: Frontmatter = { ...incoming };
  for (const k of secretFrontmatterKeys(current)) out[k] = current[k];
  return out;
}

/**
 * Text, den ein Spieler speichert: Unverändert gegenüber seiner (gefilterten) Ansicht → nichts tun.
 * Enthält der Serverstand Geheimnisse, darf der Spieler ihn nicht überschreiben (sie gingen verloren).
 */
export function playerBodyChange(currentBody: string, incoming: string | undefined): { body?: string; blocked: boolean } {
  if (incoming === undefined || incoming === currentBody || incoming === stripSecrets(currentBody)) return { blocked: false };
  if (hasSecrets(currentBody)) return { blocked: true };
  return { body: incoming, blocked: false };
}

/** Sichtbarkeit, die ein Spieler für seine eigene Notiz wählen darf: privat (mit DM) oder für alle. */
export function playerVisibility(
  requested: Visibility | undefined,
  requestedIds: string[] | null | undefined,
  selfId: string,
): { visibility: Visibility; visiblePlayerIds: string[] | null } | null {
  if (requested === undefined) return null;
  if (requested === 'all') return { visibility: 'all', visiblePlayerIds: null };
  if (requested === 'players') return { visibility: 'players', visiblePlayerIds: [...new Set([selfId, ...(requestedIds ?? [])])] };
  // „nur DM“ würde die eigene Notiz vor sich selbst verstecken
  return { visibility: 'players', visiblePlayerIds: [selfId] };
}

/** Der Besitzer sieht seine Notiz immer (Invariante beim Speichern). */
export function ensureOwnerVisible(
  visibility: Visibility,
  ids: string[] | null,
  ownerId: string | null,
): { visibility: Visibility; visiblePlayerIds: string[] | null } {
  if (!ownerId || visibility === 'all') return { visibility, visiblePlayerIds: ids };
  if (visibility === 'players') return { visibility, visiblePlayerIds: (ids ?? []).includes(ownerId) ? ids : [...(ids ?? []), ownerId] };
  return { visibility: 'players', visiblePlayerIds: [ownerId] };
}
